import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";

const EU_COUNTRIES = [
  { code: "IT", label: "Italia" }, { code: "DE", label: "Germania" }, { code: "FR", label: "Francia" },
  { code: "ES", label: "Spagna" }, { code: "AT", label: "Austria" }, { code: "BE", label: "Belgio" },
  { code: "BG", label: "Bulgaria" }, { code: "HR", label: "Croazia" }, { code: "CY", label: "Cipro" },
  { code: "CZ", label: "Repubblica Ceca" }, { code: "DK", label: "Danimarca" }, { code: "EE", label: "Estonia" },
  { code: "FI", label: "Finlandia" }, { code: "GR", label: "Grecia" }, { code: "HU", label: "Ungheria" },
  { code: "IE", label: "Irlanda" }, { code: "LV", label: "Lettonia" }, { code: "LT", label: "Lituania" },
  { code: "LU", label: "Lussemburgo" }, { code: "MT", label: "Malta" }, { code: "NL", label: "Paesi Bassi" },
  { code: "PL", label: "Polonia" }, { code: "PT", label: "Portogallo" }, { code: "RO", label: "Romania" },
  { code: "SK", label: "Slovacchia" }, { code: "SI", label: "Slovenia" }, { code: "SE", label: "Svezia" },
  { code: "GB", label: "Regno Unito" }, { code: "CH", label: "Svizzera" }, { code: "NO", label: "Norvegia" },
  { code: "IS", label: "Islanda" }, { code: "LI", label: "Liechtenstein" },
];

const EMPTY = {
  email: "",
  name: "",
  locale: "it",
  type: "individual",
  legal_form: "",
  tax_country: "IT",
  vat_id: "",
  tax_code: "",
  address_line1: "",
  address_line2: "",
  city: "",
  postal_code: "",
  address_country: "IT",
  phone: "",
  iban: "",
  bic: "",
  bank_beneficiary: "",
  commission_pct_annual: "0.5",
  withholding_pct: "0",
  notes: "",
};

export default function AdminIntroducersPage() {
  const [introducers, setIntroducers] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState(null); // opened introducer detail
  const [statements, setStatements] = useState([]);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [stmtErr, setStmtErr] = useState("");
  const [publishing, setPublishing] = useState(false);
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [stmtForm, setStmtForm] = useState({
    year: lastMonth.getFullYear(),
    month: lastMonth.getMonth() + 1,
  });

  const load = useCallback(
    () =>
      api
        .get("/admin/introducers")
        .then(({ data }) => setIntroducers(data))
        .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const body = {
        email: form.email.trim(),
        name: form.name.trim(),
        locale: form.locale,
        type: form.type,
        legal_form: form.legal_form.trim() || null,
        tax_country: form.tax_country,
        vat_id: form.vat_id.trim(),
        tax_code: form.tax_code.trim() || null,
        address_line1: form.address_line1.trim(),
        address_line2: form.address_line2.trim() || null,
        city: form.city.trim(),
        postal_code: form.postal_code.trim(),
        address_country: form.address_country,
        phone: form.phone.trim() || null,
        iban: form.iban.trim(),
        bic: form.bic.trim() || null,
        bank_beneficiary: form.bank_beneficiary.trim() || null,
        commission_pct_annual: parseFloat(form.commission_pct_annual),
        withholding_pct: parseFloat(form.withholding_pct || "0"),
        notes: form.notes.trim() || null,
      };
      await api.post("/admin/introducers", body);
      setForm(EMPTY);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/introducers/${id}`);
      setDetail(data);
      await loadStatements(id);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const loadStatements = async (introId) => {
    setStmtLoading(true);
    setStmtErr("");
    try {
      const { data } = await api.get(`/admin/introducers/${introId}/statements`);
      setStatements(data);
    } catch (err) {
      setStmtErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setStmtLoading(false);
    }
  };

  const publishStatement = async () => {
    if (!detail) return;
    setStmtErr("");
    setPublishing(true);
    try {
      await api.post(`/admin/introducers/${detail.id}/statements`, {
        year: Number(stmtForm.year),
        month: Number(stmtForm.month),
      });
      await loadStatements(detail.id);
    } catch (err) {
      setStmtErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPublishing(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo introducer? (non consentito se ha referral attivi)")) return;
    try {
      await api.delete(`/admin/introducers/${id}`);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/admin/introducers/${item.id}`, { status: newStatus });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const fmtEur = (n) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 })
      .format(Number(n || 0));

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="admin-introducers-page">
        <div className="br-fade-up flex flex-wrap justify-between items-start gap-4">
          <div>
            <div className="br-overline">Introducer</div>
            <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
              Rete <em>partner</em>.
            </h1>
            <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[720px]">
              Registra referral partner (persone fisiche o giuridiche EU/EEA). Ogni introducer riceve un codice
              promo immutabile e una % di commissione annua sull'AUM che porta.
            </p>
          </div>
          <Link
            to="/admin/referral-leads"
            className="br-btn-outline whitespace-nowrap"
            style={{ padding: "10px 18px", fontSize: "0.72rem" }}
            data-testid="btn-open-referral-leads"
          >
            Lead Referral →
          </Link>
        </div>

        {error && (
          <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="intro-err">
            {error}
          </div>
        )}

        {/* Create form */}
        <div className="mt-10 br-card px-8 py-8 br-fade-up br-delay-1" data-testid="intro-create-form">
          <div className="br-overline">Nuovo introducer</div>
          <h2 className="font-serif-display text-[1.8rem] mt-3">Anagrafica &amp; contratto</h2>

          <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Tipo soggetto">
              <select className="br-input" value={form.type} onChange={(e) => setField("type", e.target.value)} data-testid="intro-type">
                <option value="individual">Persona fisica</option>
                <option value="entity">Persona giuridica</option>
              </select>
            </Field>
            <Field label={form.type === "entity" ? "Ragione sociale" : "Nome e cognome"} required>
              <input className="br-input" value={form.name} onChange={(e) => setField("name", e.target.value)} required data-testid="intro-name" />
            </Field>
            {form.type === "entity" && (
              <Field label="Forma giuridica" required>
                <input className="br-input" placeholder="SRL, GmbH, SARL, SL…" value={form.legal_form} onChange={(e) => setField("legal_form", e.target.value)} required data-testid="intro-legal-form" />
              </Field>
            )}
            <Field label="Email (login)" required>
              <input type="email" className="br-input" value={form.email} onChange={(e) => setField("email", e.target.value)} required data-testid="intro-email" />
            </Field>
            <Field label="Telefono">
              <input className="br-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="intro-phone" />
            </Field>
            <Field label="Lingua preferita">
              <select className="br-input" value={form.locale} onChange={(e) => setField("locale", e.target.value)} data-testid="intro-locale">
                <option value="it">Italiano</option>
                <option value="en">English</option>
                <option value="no">Norsk</option>
                <option value="de">Deutsch</option>
              </select>
            </Field>

            <div className="md:col-span-2 mt-4 pt-4 border-t" style={{ borderColor: "var(--br-line)" }}>
              <div className="br-overline">Dati fiscali</div>
            </div>
            <Field label="Paese di residenza fiscale" required>
              <select className="br-input" value={form.tax_country} onChange={(e) => setField("tax_country", e.target.value)} required data-testid="intro-tax-country">
                {EU_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                ))}
              </select>
            </Field>
            <Field label="P.IVA / VAT ID" required hint={`Formato ${form.tax_country}: es. ${vatExample(form.tax_country)}`}>
              <input className="br-input font-mono-fin" value={form.vat_id} onChange={(e) => setField("vat_id", e.target.value.toUpperCase())} required data-testid="intro-vat" />
            </Field>
            <Field label="Codice fiscale (facoltativo)">
              <input className="br-input font-mono-fin" value={form.tax_code} onChange={(e) => setField("tax_code", e.target.value.toUpperCase())} data-testid="intro-tax-code" />
            </Field>

            <div className="md:col-span-2 mt-4 pt-4 border-t" style={{ borderColor: "var(--br-line)" }}>
              <div className="br-overline">Indirizzo</div>
            </div>
            <Field label="Indirizzo (via)" required>
              <input className="br-input" value={form.address_line1} onChange={(e) => setField("address_line1", e.target.value)} required data-testid="intro-address-1" />
            </Field>
            <Field label="Indirizzo (2ª riga)">
              <input className="br-input" value={form.address_line2} onChange={(e) => setField("address_line2", e.target.value)} data-testid="intro-address-2" />
            </Field>
            <Field label="Città" required>
              <input className="br-input" value={form.city} onChange={(e) => setField("city", e.target.value)} required data-testid="intro-city" />
            </Field>
            <Field label="CAP" required>
              <input className="br-input" value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)} required data-testid="intro-postal" />
            </Field>
            <Field label="Paese indirizzo" required>
              <select className="br-input" value={form.address_country} onChange={(e) => setField("address_country", e.target.value)} required data-testid="intro-address-country">
                {EU_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                ))}
              </select>
            </Field>

            <div className="md:col-span-2 mt-4 pt-4 border-t" style={{ borderColor: "var(--br-line)" }}>
              <div className="br-overline">Coordinate bancarie (SEPA)</div>
            </div>
            <Field label="IBAN" required hint="Verifica checksum ISO 13616">
              <input className="br-input font-mono-fin" value={form.iban} onChange={(e) => setField("iban", e.target.value.toUpperCase().replace(/\s+/g, ""))} required data-testid="intro-iban" />
            </Field>
            <Field label="BIC / SWIFT">
              <input className="br-input font-mono-fin" value={form.bic} onChange={(e) => setField("bic", e.target.value.toUpperCase())} data-testid="intro-bic" />
            </Field>
            <Field label="Intestatario conto (se diverso dal nome)">
              <input className="br-input" value={form.bank_beneficiary} onChange={(e) => setField("bank_beneficiary", e.target.value)} data-testid="intro-beneficiary" />
            </Field>

            <div className="md:col-span-2 mt-4 pt-4 border-t" style={{ borderColor: "var(--br-line)" }}>
              <div className="br-overline">Condizioni commerciali</div>
            </div>
            <Field label="Commissione annua % (sull'AUM)" required hint="es. 0.5 = 0,5% annuo">
              <input type="number" step="0.01" min="0.01" max="10" className="br-input font-mono-fin" value={form.commission_pct_annual} onChange={(e) => setField("commission_pct_annual", e.target.value)} required data-testid="intro-commission" />
            </Field>
            <Field label="Ritenuta d'acconto % (facoltativa)" hint="es. 20 per ritenuta al 20%">
              <input type="number" step="0.01" min="0" max="50" className="br-input font-mono-fin" value={form.withholding_pct} onChange={(e) => setField("withholding_pct", e.target.value)} data-testid="intro-withholding" />
            </Field>

            <div className="md:col-span-2 mt-2">
              <div
                className="text-[0.8rem] text-[color:var(--br-text-dim)] px-4 py-3 border"
                style={{ borderColor: "var(--br-line)", background: "rgba(255,255,255,0.02)" }}
              >
                <span className="font-mono-fin text-[0.62rem] tracking-[0.2em] uppercase" style={{ color: "var(--br-bronze)" }}>Contratto:</span>{" "}
                al momento della creazione, l'Introducer riceverà via email il PDF del contratto e potrà sottoscriverlo digitalmente dalla sua area riservata. Non è previsto flag manuale di firma.
              </div>
            </div>

            <div className="md:col-span-2">
              <Field label="Note interne (facoltative)">
                <textarea className="br-input" rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="intro-notes" />
              </Field>
            </div>

            <div className="md:col-span-2 mt-4">
              <button type="submit" className="br-btn-primary" disabled={submitting} data-testid="intro-submit">
                {submitting ? "Creazione…" : "Crea introducer & invia invito"}
              </button>
            </div>
          </form>
        </div>

        {/* List */}
        <div className="mt-10 br-card overflow-x-auto br-fade-up br-delay-2" data-testid="intro-list">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>Nome</Th>
                <Th>Codice</Th>
                <Th>Paese</Th>
                <Th>Comm. %</Th>
                <Th align="right">Referral</Th>
                <Th align="right">AUM</Th>
                <Th align="right">Maturato netto</Th>
                <Th>Stato</Th>
                <Th align="right">Azioni</Th>
              </tr>
            </thead>
            <tbody>
              {introducers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-10 text-center text-[color:var(--br-text-dim)]">
                    Nessun introducer registrato.
                  </td>
                </tr>
              ) : (
                introducers.map((i) => (
                  <tr key={i.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`intro-row-${i.id}`}>
                    <Td>
                      <div className="text-[color:var(--br-parchment)] font-serif-display italic text-[1.05rem]">{i.full_name}</div>
                      <div className="text-[0.72rem] text-[color:var(--br-text-dim)] font-mono-fin">{i.email}</div>
                    </Td>
                    <Td className="font-mono-fin text-[0.82rem]" style={{ color: "var(--br-bronze)" }}>{i.code}</Td>
                    <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{i.tax_country}</Td>
                    <Td className="font-mono-fin">{i.commission_pct_annual}%</Td>
                    <Td align="right" className="font-mono-fin">{i.stats.referrals_active} / {i.stats.referrals_total}</Td>
                    <Td align="right" className="font-mono-fin">{fmtEur(i.stats.aum_referred)}</Td>
                    <Td align="right" className="font-mono-fin" style={{ color: "var(--br-bronze-soft)" }}>{fmtEur(i.stats.accrued_net)}</Td>
                    <Td>
                      <span
                        className="font-mono-fin text-[0.62rem] tracking-[0.16em] uppercase px-2 py-1"
                        style={{
                          color: i.status === "active" ? "var(--br-bronze-soft)" : "var(--br-red)",
                          border: `1px solid ${i.status === "active" ? "var(--br-bronze)" : "var(--br-red)"}`,
                        }}
                      >
                        {i.status}
                      </span>
                    </Td>
                    <Td align="right">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => openDetail(i.id)} className="br-btn-outline" style={{ padding: "6px 10px", fontSize: "0.72rem" }} data-testid={`intro-detail-${i.id}`}>Dettagli</button>
                        <button onClick={() => toggleStatus(i)} className="br-btn-outline" style={{ padding: "6px 10px", fontSize: "0.72rem" }} data-testid={`intro-toggle-${i.id}`}>
                          {i.status === "active" ? "Sospendi" : "Riattiva"}
                        </button>
                        <button onClick={() => remove(i.id)} className="br-btn-outline" style={{ padding: "6px 10px", fontSize: "0.72rem", color: "var(--br-red)", borderColor: "var(--br-red)" }} data-testid={`intro-delete-${i.id}`}>Elimina</button>
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Detail modal */}
        {detail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => { setDetail(null); setStatements([]); setStmtErr(""); }} data-testid="intro-detail-modal">
            <div className="br-card max-w-[900px] w-full max-h-[85vh] overflow-y-auto px-8 py-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="br-overline">{detail.code}</div>
                  <h2 className="font-serif-display text-[2rem] mt-2">{detail.full_name}</h2>
                  <p className="text-[0.85rem] text-[color:var(--br-text-dim)] mt-1">{detail.email}</p>
                </div>
                <button onClick={() => { setDetail(null); setStatements([]); setStmtErr(""); }} className="text-[color:var(--br-text-dim)] hover:text-[color:var(--br-parchment)]" data-testid="intro-close-detail">✕</button>
              </div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatBox label="Referral totali" value={String(detail.stats.referrals_total)} />
                <StatBox label="Attivi" value={String(detail.stats.referrals_active)} />
                <StatBox label="AUM portato" value={fmtEur(detail.stats.aum_referred)} />
                <StatBox label="Maturato netto" value={fmtEur(detail.stats.accrued_net)} />
              </div>

              <div className="mt-8">
                <div className="br-overline">Referral (visibile solo all'admin)</div>
                <table className="w-full mt-3 text-[0.85rem]">
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                      <th className="text-left py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Cliente</th>
                      <th className="text-left py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Stato</th>
                      <th className="text-right py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Principale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.referrals.length === 0 ? (
                      <tr><td colSpan={3} className="py-4 text-center text-[color:var(--br-text-dim)]">Nessun cliente referral ancora.</td></tr>
                    ) : detail.referrals.map((r) => (
                      <tr key={r.user_id} className="border-b" style={{ borderColor: "var(--br-line)" }}>
                        <td className="py-2">
                          <div className="text-[color:var(--br-parchment)]">{r.name}</div>
                          <div className="text-[0.7rem] text-[color:var(--br-text-dim)]">{r.email}</div>
                        </td>
                        <td className="py-2 font-mono-fin text-[0.72rem]">{r.status}</td>
                        <td className="py-2 text-right font-mono-fin">{fmtEur(r.principal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-8">
                <div className="br-overline">Lead recenti</div>
                {detail.leads.length === 0 ? (
                  <p className="mt-3 text-[color:var(--br-text-dim)] text-[0.85rem]">Nessun lead ancora.</p>
                ) : (
                  <table className="w-full mt-3 text-[0.85rem]">
                    <thead>
                      <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                        <th className="text-left py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Nome</th>
                        <th className="text-left py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Email</th>
                        <th className="text-left py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Stato</th>
                        <th className="text-left py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Ricevuto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.leads.map((l) => (
                        <tr key={l.id} className="border-b" style={{ borderColor: "var(--br-line)" }}>
                          <td className="py-2">{l.name}</td>
                          <td className="py-2 text-[color:var(--br-text-dim)]">{l.email}</td>
                          <td className="py-2 font-mono-fin text-[0.72rem]">{l.status}</td>
                          <td className="py-2 font-mono-fin text-[0.72rem] text-[color:var(--br-text-dim)]">{formatDate(l.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-[0.85rem]">
                <InfoRow label="P.IVA" value={detail.vat_id} />
                <InfoRow label="Codice fiscale" value={detail.tax_code || "—"} />
                <InfoRow label="Paese fiscale" value={detail.tax_country} />
                <InfoRow label="Forma giuridica" value={detail.legal_form || "—"} />
                <InfoRow label="Indirizzo" value={`${detail.address_line1}${detail.address_line2 ? ", " + detail.address_line2 : ""}, ${detail.postal_code} ${detail.city} (${detail.address_country})`} />
                <InfoRow label="IBAN" value={detail.iban} />
                <InfoRow label="BIC" value={detail.bic || "—"} />
                <InfoRow label="Commissione %" value={`${detail.commission_pct_annual}% annuo`} />
                <InfoRow label="Ritenuta %" value={`${detail.withholding_pct || 0}%`} />
                <InfoRow label="Contratto" value={
                  <span>
                    {detail.contract_signed ? (
                      <span style={{ color: "var(--br-bronze-soft)" }}>
                        Firmato il {detail.contract_signed_at || "n.d."}
                      </span>
                    ) : (
                      <span style={{ color: "var(--br-red)" }}>Non ancora firmato</span>
                    )}
                    <a
                      href={`${(typeof window !== "undefined" && window.location.origin) || ""}/api/admin/introducers/${detail.id}/contract`}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-3 font-mono-fin text-[0.68rem] tracking-[0.18em] uppercase"
                      style={{ color: "var(--br-bronze)" }}
                      data-testid={`intro-download-contract-${detail.id}`}
                    >
                      ↓ Scarica PDF
                    </a>
                  </span>
                } />
              </div>

              {/* Monthly statements — publish + list */}
              <div className="mt-10 pt-6 border-t" style={{ borderColor: "var(--br-line)" }} data-testid="intro-statements-section">
                <div className="br-overline">Estratti mensili</div>
                <h3 className="font-serif-display text-[1.4rem] mt-2">Provvigioni maturate</h3>
                <p className="text-[0.82rem] text-[color:var(--br-text-dim)] mt-2 max-w-[640px]">
                  Genera un estratto per un mese chiuso: il PDF (con firma Borealis) viene inviato automaticamente all'introducer via email e archiviato qui.
                </p>

                {!detail.contract_signed && (
                  <div
                    className="mt-4 px-4 py-3 text-[0.82rem]"
                    style={{ border: "1px solid var(--br-red)", color: "var(--br-red)", background: "rgba(179,107,107,0.05)" }}
                    data-testid="stmt-contract-warn"
                  >
                    Contratto non ancora firmato — non è possibile pubblicare estratti finché l'introducer non firma digitalmente.
                  </div>
                )}

                <div className="mt-5 flex flex-wrap items-end gap-3" data-testid="stmt-publish-form">
                  <label className="block">
                    <span className="br-overline block mb-2">Anno</span>
                    <input
                      type="number"
                      min="2024"
                      max="2100"
                      className="br-input font-mono-fin"
                      style={{ width: 110 }}
                      value={stmtForm.year}
                      onChange={(e) => setStmtForm((p) => ({ ...p, year: e.target.value }))}
                      data-testid="stmt-year-input"
                    />
                  </label>
                  <label className="block">
                    <span className="br-overline block mb-2">Mese</span>
                    <select
                      className="br-input font-mono-fin"
                      style={{ width: 180 }}
                      value={stmtForm.month}
                      onChange={(e) => setStmtForm((p) => ({ ...p, month: e.target.value }))}
                      data-testid="stmt-month-input"
                    >
                      {["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map((m, i) => (
                        <option key={m} value={i + 1}>{String(i + 1).padStart(2, "0")} — {m}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={publishStatement}
                    disabled={publishing || !detail.contract_signed}
                    className="br-btn-primary"
                    style={{ padding: "10px 20px" }}
                    data-testid="stmt-publish-btn"
                  >
                    {publishing ? "Pubblicazione…" : "Pubblica estratto"}
                  </button>
                </div>

                {stmtErr && (
                  <div className="mt-4 br-card p-3 text-[color:var(--br-red)] text-[0.85rem]" data-testid="stmt-error">
                    {stmtErr}
                  </div>
                )}

                <div className="mt-6" data-testid="stmt-list">
                  {stmtLoading ? (
                    <p className="text-[color:var(--br-text-dim)] text-[0.85rem]">Caricamento estratti…</p>
                  ) : statements.length === 0 ? (
                    <p className="text-[color:var(--br-text-dim)] text-[0.85rem]" data-testid="stmt-empty">Nessun estratto ancora pubblicato.</p>
                  ) : (
                    <table className="w-full text-[0.85rem]">
                      <thead>
                        <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                          <th className="text-left py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Periodo</th>
                          <th className="text-left py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Numero</th>
                          <th className="text-right py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Lordo</th>
                          <th className="text-right py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">Netto</th>
                          <th className="text-right py-2 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]">PDF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statements.map((s) => (
                          <tr key={s.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`stmt-row-${s.id}`}>
                            <td className="py-2 font-serif-display italic text-[color:var(--br-parchment)]">{s.period_label}</td>
                            <td className="py-2 font-mono-fin text-[0.72rem] text-[color:var(--br-text-dim)]">{s.statement_number}</td>
                            <td className="py-2 text-right font-mono-fin">{fmtEur(s.gross_commission)}</td>
                            <td className="py-2 text-right font-mono-fin" style={{ color: "var(--br-bronze-soft)" }}>{fmtEur(s.net_amount)}</td>
                            <td className="py-2 text-right">
                              <a
                                href={`${(typeof window !== "undefined" && window.location.origin) || ""}/api/admin/introducers/${detail.id}/statements/${s.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="font-mono-fin text-[0.68rem] tracking-[0.18em] uppercase"
                                style={{ color: "var(--br-bronze)" }}
                                data-testid={`stmt-download-${s.id}`}
                              >
                                ↓ Scarica
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </PortalLayout>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="br-overline block mb-2">
        {label} {required && <span style={{ color: "var(--br-red)" }}>*</span>}
      </span>
      {children}
      {hint && <span className="block mt-1 text-[0.7rem] text-[color:var(--br-text-dim)]">{hint}</span>}
    </label>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th className="px-4 py-3 font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase text-[color:var(--br-text-dim)]" style={{ textAlign: align }}>{children}</th>
  );
}

function Td({ children, align = "left", className = "", style }) {
  return (
    <td className={`px-4 py-3 text-[0.88rem] ${className}`} style={{ textAlign: align, ...style }}>{children}</td>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="border px-3 py-3" style={{ borderColor: "var(--br-line)" }}>
      <div className="font-mono-fin text-[0.55rem] tracking-[0.2em] uppercase text-[color:var(--br-bronze)]">{label}</div>
      <div className="font-serif-display text-[1.35rem] mt-1 text-[color:var(--br-parchment)]">{value}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="font-mono-fin text-[0.55rem] tracking-[0.2em] uppercase text-[color:var(--br-bronze)]">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}

function vatExample(country) {
  const examples = {
    IT: "IT12345678901", DE: "DE123456789", FR: "FRXX999999999",
    ES: "ESA12345678", AT: "ATU12345678", BE: "BE0999999999",
    NL: "NL123456789B01", PT: "PT123456789", PL: "PL1234567890",
    GB: "GB123456789", CH: "CHE-123.456.789", NO: "NO123456789MVA",
  };
  return examples[country] || `${country}XXXXXXXXX`;
}
